import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Avatar,
  Typography,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ChevronLeftIcon    from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon   from '@mui/icons-material/ChevronRight';
import LogoutIcon         from '@mui/icons-material/Logout';
import VisibilityIcon     from '@mui/icons-material/Visibility';
import NaturePeopleIcon   from '@mui/icons-material/NaturePeople';
import SwapHorizIcon      from '@mui/icons-material/SwapHoriz';

import { useAuthContext } from '../../context/auth-context';
import { NAV_ITEMS, BOTTOM_NAV_ITEMS } from './nav-config';
import CampSelectDialog from './CampSelectDialog';
import { useViewAs, ViewAsMenu } from '../shared/ViewAsSwitcher';
import { APP_BAR_COLOR } from '../../constants/env';

const DRAWER_WIDTH    = 240;
const COLLAPSED_WIDTH = 65;

// ─── Colores del tema ─────────────────────────────────────────────────────────
const COLORS = {
  // Verde de marca en produccion, ambar en dev: el armazon entero cambia de color
  // para que no haya forma de confundir los dos entornos de un vistazo.
  bg:           APP_BAR_COLOR,
  bgHover:      'rgba(255,255,255,0.08)',
  bgActive:     'rgba(255,255,255,0.15)',
  borderActive: '#ffffff',
  text:         'rgba(255,255,255,0.85)',
  textActive:   '#ffffff',
  divider:      'rgba(255,255,255,0.12)',
  icon:         'rgba(255,255,255,0.70)',
  iconActive:   '#ffffff',
};

function NavItem({ title, path, icon: Icon, open, active, onClick, disabled }) {
  return (
    <Tooltip title={open ? (disabled ? 'Selecciona un campamento primero' : '') : title} placement="right" arrow>
      <ListItem disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          onClick={disabled ? undefined : onClick}
          sx={{
            minHeight: 48,
            justifyContent: open ? 'initial' : 'center',
            px: 2,
            borderLeft: `3px solid ${active && !disabled ? COLORS.borderActive : 'transparent'}`,
            backgroundColor: active && !disabled ? COLORS.bgActive : 'transparent',
            borderRadius: '0 8px 8px 0',
            mr: 1,
            opacity: disabled ? 0.35 : 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
            pointerEvents: disabled ? 'none' : 'auto',
            '&:hover': { backgroundColor: disabled ? 'transparent' : COLORS.bgHover },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 2 : 'auto',
              justifyContent: 'center',
              color: active && !disabled ? COLORS.iconActive : COLORS.icon,
            }}
          >
            <Icon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={title}
            sx={{
              opacity: open ? 1 : 0,
              transition: 'opacity 0.2s',
              '& .MuiTypography-root': {
                fontSize: '0.875rem',
                fontWeight: active && !disabled ? 600 : 400,
                color: active && !disabled ? COLORS.textActive : COLORS.text,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
        </ListItemButton>
      </ListItem>
    </Tooltip>
  );
}

function Sidebar({ mobileOpen = false, onMobileClose = () => {} }) {
  const [open, setOpen]               = useState(true);
  const [campDialogOpen, setCampDialogOpen] = useState(false);
  // "Ver como" entra por aquí: es un control de identidad y el bloque de perfil, justo
  // debajo, es donde vive el resto de la identidad. El aviso de que la vista previa
  // está activa NO se pinta aquí — va en el flujo del contenido (`ViewAsBanner`),
  // porque en móvil este sidebar es un drawer y con él cerrado no se vería.
  const [viewAsAnchor, setViewAsAnchor] = useState(null);
  const viewAs = useViewAs(Boolean(viewAsAnchor));
  const navigate                      = useNavigate();
  const location                      = useLocation();
  const { logout, user, activeCamp, hasPermission }  = useAuthContext();

  // Un ítem sin `perm` lo ve cualquiera que haya iniciado sesión.
  const visible = items => items.filter(({ perm }) => !perm || hasPermission(perm));
  const theme                         = useTheme();
  const isMobile                      = useMediaQuery(theme.breakpoints.down('md'));

  const isActive = (path) => {
    if (path === '/user') return location.pathname === '/' || location.pathname === '/user';
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };

  const username     = typeof user === 'object' ? (user?.username || user?.fullname || 'Usuario') : (user || 'Usuario');
  const avatarLetter = username[0]?.toUpperCase() ?? 'U';

  const drawerWidth = isMobile ? DRAWER_WIDTH : (open ? DRAWER_WIDTH : COLLAPSED_WIDTH);

  const handleNavClick = (path) => {
    navigate(path);
    if (isMobile) onMobileClose();
  };

  return (
    <>
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={isMobile ? onMobileClose : undefined}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? 0 : drawerWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            overflowX: 'hidden',
            backgroundColor: COLORS.bg,
            borderRight: 'none',
            boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: open
                  ? theme.transitions.duration.enteringScreen
                  : theme.transitions.duration.leavingScreen,
              }),
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* ── Cabecera / toggle ─────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: (isMobile || open) ? 'space-between' : 'center',
            minHeight: 64,
            px: (isMobile || open) ? 2 : 0,
            flexShrink: 0,
          }}
        >
          {(isMobile || open) && (
            <Typography
              variant="subtitle1"
              sx={{ color: COLORS.textActive, fontWeight: 700, letterSpacing: 0.5 }}
            >
              Camp Manager
            </Typography>
          )}
          {!isMobile && (
            <IconButton onClick={() => setOpen((v) => !v)} sx={{ color: COLORS.text }} size="small">
              {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          )}
        </Box>

        {/* ── Camp activo ───────────────────────────────────────────────── */}
        <Tooltip
          title={open ? 'Cambiar campamento' : activeCamp?.name ?? 'Sin campamento'}
          placement="right"
          arrow
        >
          <Box
            onClick={() => setCampDialogOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: open ? 1 : 0,
              px: open ? 2 : 0,
              py: 1,
              mx: open ? 1 : 'auto',
              mb: 0.5,
              borderRadius: 2,
              cursor: 'pointer',
              justifyContent: open ? 'flex-start' : 'center',
              backgroundColor: 'rgba(255,255,255,0.08)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
              transition: 'background-color 0.2s',
              overflow: 'hidden',
            }}
          >
            {activeCamp?.logoUrl ? (
              <Avatar src={activeCamp.logoUrl} sx={{ width: 22, height: 22, flexShrink: 0 }} />
            ) : (
              <NaturePeopleIcon fontSize="small" sx={{ color: COLORS.textActive, flexShrink: 0 }} />
            )}
            {open && (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    color: COLORS.textActive,
                    fontWeight: 600,
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.4,
                  }}
                >
                  {activeCamp?.name ?? 'Sin campamento'}
                </Typography>
                <SwapHorizIcon fontSize="small" sx={{ color: COLORS.icon, flexShrink: 0 }} />
              </>
            )}
          </Box>
        </Tooltip>

        <Divider sx={{ borderColor: COLORS.divider, flexShrink: 0 }} />

        {/* ── Ítems principales ─────────────────────────────────────────── */}
        <List sx={{ pt: 1, flex: 1 }}>
          {visible(NAV_ITEMS).map(({ title, path, icon }) => (
            <NavItem
              key={path}
              title={title}
              path={path}
              icon={icon}
              open={isMobile ? true : open}
              active={isActive(path)}
              onClick={() => handleNavClick(path)}
              disabled={!activeCamp}
            />
          ))}
        </List>

        {/* ── Ítems inferiores (opcionales) ─────────────────────────────── */}
        {visible(BOTTOM_NAV_ITEMS).length > 0 && (
          <>
            <Divider sx={{ borderColor: COLORS.divider }} />
            <List>
              {visible(BOTTOM_NAV_ITEMS).map(({ title, path, icon }) => (
                <NavItem
                  key={path}
                  title={title}
                  path={path}
                  icon={icon}
                  open={isMobile ? true : open}
                  active={isActive(path)}
                  onClick={() => handleNavClick(path)}
                  disabled={!activeCamp}
                />
              ))}
            </List>
          </>
        )}

        {/* ── "Ver como" ───────────────────────────────────────────────── */}
        {viewAs.puedeVerComo && (
          <>
            <Divider sx={{ borderColor: COLORS.divider }} />
            <List sx={{ py: 0 }}>
              <NavItem
                title="Ver como"
                path="__view-as"
                icon={VisibilityIcon}
                open={isMobile ? true : open}
                active={Boolean(viewAs.current)}
                onClick={e => setViewAsAnchor(e.currentTarget)}
              />
            </List>
          </>
        )}

        {/* ── Perfil de usuario + logout ────────────────────────────────── */}
        <Divider sx={{ borderColor: COLORS.divider }} />
        <Tooltip
          title={open ? 'Cambiar campamento' : username}
          placement="right"
          arrow
          disableHoverListener={open}
        >
          <Box
            onClick={() => setCampDialogOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: (isMobile || open) ? 1.5 : 1,
              justifyContent: (isMobile || open) ? 'flex-start' : 'center',
              flexShrink: 0,
              cursor: 'pointer',
              '&:hover': { backgroundColor: COLORS.bgHover },
              transition: 'background-color 0.2s',
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'rgba(255,255,255,0.2)',
                fontSize: '0.8rem',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {avatarLetter}
            </Avatar>

            {(isMobile || open) && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: COLORS.text,
                    fontSize: '0.8rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                  }}
                >
                  {username}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: COLORS.icon,
                    fontSize: '0.7rem',
                    lineHeight: 1.2,
                  }}
                >
                  Cambiar campamento
                </Typography>
              </Box>
            )}

            <Tooltip title="Cerrar sesión" placement={open ? 'top' : 'right'} arrow>
              <IconButton
                onClick={(e) => { e.stopPropagation(); handleLogout(); }}
                size="small"
                sx={{ color: COLORS.icon, '&:hover': { color: COLORS.textActive } }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Tooltip>
      </Drawer>

      <CampSelectDialog
        open={campDialogOpen}
        onClose={() => setCampDialogOpen(false)}
      />

      <ViewAsMenu anchor={viewAsAnchor} onClose={() => setViewAsAnchor(null)} {...viewAs} />
    </>
  );
}

export default Sidebar;
